import { z } from 'zod';

/**
 * Runtime configuration, read from `window.__OVERSEER_CONFIG__`.
 *
 * `public/config.js` sets it before the bundle loads, so one build artefact
 * serves local, dev and production, and deployment replaces one file rather
 * than baking a URL into the build.
 *
 * Validated with zod and **thrown on**, not defaulted. A misconfigured deploy
 * must fail loudly on the first paint rather than quietly talk to the wrong API
 * — which, with two environments sharing a database engine, is how test data
 * ends up in production.
 */
const configSchema = z.object({
  apiUrl: z.string().url(),
  cognito: z.object({
    region: z.string().min(1),
    userPoolId: z.string().min(1),
    clientId: z.string().min(1),
    /** Set only against an emulator; absent in production. */
    endpoint: z.string().url().optional(),
  }),
});

export type AppConfig = z.infer<typeof configSchema>;

declare global {
  interface Window {
    __OVERSEER_CONFIG__?: unknown;
  }
}

/**
 * Development-only overrides, from `VITE_*` environment variables.
 *
 * Why this is guarded by `import.meta.env.DEV`: Vite **inlines** `import.meta.env`
 * at build time, so an unguarded read would let a stray `VITE_API_URL` in a CI
 * shell get baked into a production bundle and silently outrank `config.js` —
 * defeating the entire point of runtime configuration. Guarded, the branch is
 * dead code in a production build and cannot be reached however the build was
 * invoked.
 *
 * Used for pointing a dev server or an E2E run at a different API port.
 */
const devOverrides = (runtime: unknown): unknown => {
  if (!import.meta.env.DEV) {
    return runtime;
  }

  const base = typeof runtime === 'object' && runtime !== null ? runtime : {};
  const apiUrl = import.meta.env.VITE_API_URL;

  return apiUrl ? { ...base, apiUrl } : base;
};

const parse = (): AppConfig => {
  const result = configSchema.safeParse(devOverrides(window.__OVERSEER_CONFIG__));

  if (!result.success) {
    const problems = result.error.issues
      .map((issue) => `  - ${issue.path.join('.') || '(root)'}: ${issue.message}`)
      .join('\n');

    throw new Error(
      `Invalid runtime configuration in public/config.js:\n${problems}\n\n` +
        'That file is loaded before the bundle and replaced per environment.',
    );
  }

  return result.data;
};

/**
 * The parsed configuration, read once on first use.
 *
 * Lazy, not a module-level `const`. Parsing at import time meant that *importing*
 * anything downstream of this file — `api/client.ts`, for instance — threw in any
 * environment without a `config.js`, so the API layer could not be unit tested at
 * all. A module whose import has a side effect that can fail is a module nothing
 * else can be tested around.
 *
 * The "fail loudly at boot" property is kept by `assertConfig()`, which `main.tsx`
 * calls before rendering. Failing on the first paint rather than at the first
 * `import` is the same guarantee, made deliberately.
 */
let cached: AppConfig | undefined;

export const config = (): AppConfig => {
  cached ??= parse();

  return cached;
};

/** Validates now, so a misconfigured deploy fails on the first paint. */
export const assertConfig = (): void => {
  config();
};

/** `http://localhost:3000` + `/v1/me` → one place that knows about slashes. */
export const apiUrl = (path: string): string =>
  `${config().apiUrl.replace(/\/$/, '')}/${path.replace(/^\//, '')}`;

/** True when pointed at a Cognito emulator rather than real AWS. */
export const usingCognitoEmulator = (): boolean => Boolean(config().cognito.endpoint);
